import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { authService, usertourService } from '@/services';
import { B2B_ROLE, AUTH_MESSAGES } from '@petseo/shared';
import type { User } from '@/services/auth.service';
import { useProfileStore } from './profileStore';
import { useServiceVendorFiltersStore } from '@/features/service-vendors/store/serviceVendorFiltersStore';
import { queryClient } from '@/providers/queryClient';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  registrationSuccess: boolean;
  roleConfirmationSent: boolean;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => Promise<void>;
  clearError: () => void;
  clearAuthNotices: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  subscribeWithSelector((set, get) => ({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isInitialized: false,
    error: null,
    registrationSuccess: false,
    roleConfirmationSent: false,

    setLoading: (loading: boolean) => set({ isLoading: loading }),

    clearError: () => set({ error: null }),

    clearAuthNotices: () => set({ registrationSuccess: false, roleConfirmationSent: false }),

    login: async (email: string, password: string) => {
      const { setLoading, clearError, clearAuthNotices } = get();
      setLoading(true);
      clearError();
      clearAuthNotices();

      try {
        const result = await authService.login({ email, password });

        if (result.success) {
          const roles = await authService.userAssociatedRoles();

          if (!roles.includes(B2B_ROLE)) {
            await authService.logout();
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: AUTH_MESSAGES.NO_B2B_PERMISSION,
            });
            throw new Error(AUTH_MESSAGES.NO_B2B_PERMISSION);
          }

          const emailVerified = await authService.isEmailVerified();
          if (!emailVerified) {
            await authService.logout();
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: AUTH_MESSAGES.EMAIL_NOT_VERIFIED,
            });
            throw new Error(AUTH_MESSAGES.EMAIL_NOT_VERIFIED);
          }

          set({
            user: result.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : AUTH_MESSAGES.LOGIN_FAILED,
        });
        throw error;
      }
    },

    register: async (email: string, password: string) => {
      const { setLoading, clearError, clearAuthNotices } = get();
      setLoading(true);
      clearError();
      clearAuthNotices();

      try {
        const result = await authService.register({ email, password });

        if (result && 'roleConfirmationSent' in result) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            registrationSuccess: false,
            roleConfirmationSent: true,
          });
          return;
        }

        if (result?.success) {
          await authService.logout();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            registrationSuccess: true,
            roleConfirmationSent: false,
          });
        }
      } catch (error) {
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : AUTH_MESSAGES.REGISTER_FAILED,
        });
        throw error;
      }
    },

    logout: async () => {
      const { setLoading } = get();
      setLoading(true);

      try {
        await authService.logout();

        useProfileStore.getState().reset();
        useServiceVendorFiltersStore.getState().clearFilters();
        queryClient.clear();
        usertourService.reset();

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : AUTH_MESSAGES.LOGOUT_FAILED,
        });
      }
    },

    initialize: async () => {
      const { isInitialized } = get();
      if (isInitialized) return;

      set({ isLoading: true });

      try {
        const sessionExists = await authService.isAuthenticated();

        if (sessionExists) {
          const currentUser = await authService.getCurrentUser();
          const roles = await authService.userAssociatedRoles();

          const emailVerified = await authService.isEmailVerified();

          if (roles.includes(B2B_ROLE) && currentUser && emailVerified) {
            set({
              user: currentUser,
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
              error: null,
            });
          } else {
            await authService.logout();
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              isInitialized: true,
              error: null,
            });
          }
        } else {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            error: null,
          });
        }
      } catch (error) {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true,
          error: error instanceof Error ? error.message : AUTH_MESSAGES.AUTH_CHECK_FAILED,
        });
      }
    },
  }))
);
